import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Quote, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, media } from "../lib/api";
import { PILLARS, pillarMeta } from "../lib/constants";

export default function CitizenVoices() {
  const [voices, setVoices] = useState([]);
  const [name, setName] = useState("");
  const [story, setStory] = useState("");
  const [category, setCategory] = useState("citizen_voices");
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/submissions").then((r) => setVoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !story.trim()) { toast.error("Please add your name and your story"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("story", story.trim());
      form.append("category", category);
      if (photo) form.append("photo", photo);
      const res = await api.post("/submissions", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      setName(""); setStory(""); setPhoto(null);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="voices" className="relative py-24 bg-[#050B14] grain" data-testid="voices-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-12">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF6B00] mb-3">Listen. Share. Be Heard.</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Citizen Voices</h2>
          <p className="mt-4 text-[#94A3B8] max-w-2xl">
            Everyday people. Real experiences. Real impact. Share your story with photo or use the Live Mic above —
            approved voices get featured on the show.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Submission form */}
          <form
            onSubmit={submit}
            data-testid="citizen-voices-form"
            className="lg:col-span-2 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 sm:p-8 h-fit"
          >
            <h3 className="font-cond font-bold uppercase text-xl mb-5">Share Your Story</h3>
            <div className="space-y-4">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (or Anonymous)"
                data-testid="citizen-name-input"
                className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]"
              />
              <textarea
                value={story}
                onChange={(e) => setStory(e.target.value)}
                placeholder="Tell us your experience..."
                rows={5}
                data-testid="citizen-story-input"
                className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF] resize-none"
              />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="citizen-category-select"
                className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]"
              >
                {PILLARS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <label
                data-testid="citizen-photo-upload"
                className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-[#0066FF]/35 px-3.5 py-3 text-sm text-[#94A3B8] hover:border-[#0066FF] transition-colors"
              >
                <Upload className="h-4 w-4" />
                {photo ? photo.name : "Attach a photo (optional)"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="citizen-submit-button"
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase tracking-wide text-white blue-box-glow"
              >
                <Send className="h-4 w-4" /> {submitting ? "Submitting..." : "Submit My Story"}
              </button>
            </div>
          </form>

          {/* Approved voices */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5 content-start" data-testid="citizen-voices-list">
            {voices.length === 0 && (
              <div className="sm:col-span-2 text-[#94A3B8] font-mono2 uppercase text-sm py-10 text-center">
                Be the first voice to be heard
              </div>
            )}
            {voices.map((v, i) => {
              const meta = pillarMeta(v.category);
              return (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 2) * 0.1 }}
                  data-testid={`voice-card-${v.id}`}
                  className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-5"
                >
                  <Quote className="h-6 w-6 mb-3" style={{ color: meta.color }} />
                  <p className="text-sm text-[#CBD5E1] leading-relaxed mb-4">{v.story}</p>
                  {v.photo_file_id && (
                    <img
                      src={media(v.photo_file_id, null)}
                      alt={v.name}
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                  )}
                  {v.audio_file_id && (
                    <audio
                      src={media(v.audio_file_id, null)}
                      controls
                      className="w-full mb-4 h-9"
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-cond font-bold uppercase text-sm">{v.name}</span>
                    <span
                      className="font-mono2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: `${meta.color}22`, color: meta.color }}
                    >
                      {meta.short}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
